import React, { FunctionComponent, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { Box, Button, Fab, Menu, MenuItem, Tooltip } from '@material-ui/core';
import BackToTopArrow from '@material-ui/icons/ArrowUpward';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { GetApp as DownloadIcon } from '@material-ui/icons';
import InfiniteScrollTableView from '../../../components/InfiniteScrollTableView';
import {
    MergeTemplate,
    User,
    Order,
    FilterCondition,
    ConfiguredFields,
    IColumn,
} from '../../../store/modules/application/types';
import { Person } from '../../../store/modules/person/types';

import { BASE_URL } from '../../../routes';
import { EventList } from '../../../store/modules/calendar/types';
import TableEmptyState from '../../../components/TableView/TableEmptyState';
import { getFlatColumnFieldsFromList, getFormattedColumns, getFullAddress } from '../../../utils/utils';
declare module '@material-ui/core/styles/withStyles' {
    // Augment the BaseCSSProperties so that we can control jss-rtl
    interface BaseCSSProperties {
        /*
         * Used to control if the rule-set should be affected by rtl transformation
         */
        flip?: boolean;
    }
}

type EventProps = {
    eventList: EventList;
    isLoading: boolean;
    skip: number;
    setSkip: Function;
    limit: number;
    hasMore: boolean;
    setError: Function;
    isFiltersLoading: boolean;
    applyEventFilters: Function;
    listFilters: FilterCondition[];
    mergeTemplatesList: MergeTemplate[];
    mergeInspectorPackDocuments: Function;
    sortOrder: Order;
    sortBy: string;
    setSortOrder: Function;
    setSortColumn: Function;
    calendarColumnFields: ConfiguredFields;
};

const componentStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            padding: '0px 40px 40px 40px',
        },
        tablePaginationInfo: {
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px',
        },
        addPersonInfo: {
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '10px',
            '& span': {
                margin: '0 8px',
            },
        },
        container: {
            display: 'flex',
            width: '100%',
            backgroundColor: 'white',
        },
        fab: {
            position: 'absolute',
            bottom: 55,
            left: 'calc( 100vw - 100px)',
            zIndex: 3,
            backgroundColor: 'white',
        },
        searchIconContainer: {
            width: '100%',
            maxHeight: 300,
            color: 'gray',
            paddingTop: '40px',
            paddingBottom: '150px',
        },
        noResultFound: {
            fontSize: '20px',
            color: '#484848',
            lineHeight: '24px',
            fontWeight: 500,
            marginTop: '30vh',
        },
        countSpan: {
            color: '#484848',
            fontSize: 14,
            letterSpacing: 0.1,
            display: 'flex',
            alignItems: 'center',
        },
        loadingBar: {
            width: 'calc(100vw - 100px)',
            position: 'absolute',
            bottom: 45,
            zIndex: 3,
            background: 'white',
            paddingLeft: 13,
            paddingBottom: 5,
        },
        addLicensee: {
            letterSpacing: 0.1,
        },
        exportLicense: {
            display: 'flex',
            fontSize: 14,
            letterSpacing: 0.1,
            alignItems: 'center',
            padding: '10px',
        },
        modalBtn: {
            marginLeft: 0,
        },
        exportBtn: {
            padding: '0px',
            'text-transform': 'initial',
            fontWeight: 500,
            cursor: 'pointer',
            color: theme.palette.primary.main,
        },
        metadataContainer: {
            display: 'flex',
            justifyContent: 'space-between',
        },
        MenuList: {
            marginTop: 46,
        },
    }),
);

const EventInfiniteList: FunctionComponent<EventProps> = (props) => {
    let columns: IColumn[];
    const classes = componentStyles();
    const history = useHistory();
    const [scrollTop, setScrollTop] = useState<number>(0);
    const [open, setOpen] = React.useState(false);

    const anchorRef = React.useRef<HTMLButtonElement>(null);
    // return focus to the button when we transitioned from !open -> open
    const prevOpen = React.useRef(open);
    const {
        eventList,
        skip,
        hasMore,
        mergeTemplatesList,
        mergeInspectorPackDocuments,
        listFilters,
        setSkip,
        sortBy,
        sortOrder,
        setSortOrder,
        setSortColumn,
        calendarColumnFields,
    } = props;
    useEffect(() => {
        if (skip === 0) {
            setScrollTop(0);
        }
    }, [skip]);

    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current!.focus();
        }

        prevOpen.current = open;
    }, [open]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };
    const handleDownload = (item: any) => {
        const isInspectorPack = true;
        mergeInspectorPackDocuments(item?.id, item?.name, isInspectorPack, listFilters);
        setOpen(false);
    };

    const loadMore = () => {
        if (hasMore) {
            setSkip(skip + 1);
        }
    };
    const routeChange = (path: string) => {
        history.push(path);
    };

    const onClickRow = (id: string, e: any) => {
        e.stopPropagation();
        routeChange(BASE_URL + `/appointment/${id}/detail`);
    };

    columns = calendarColumnFields?.fields?.length
        ? getFormattedColumns(calendarColumnFields)
        : [
              {
                  id: 'appointmentDateTime|dateTimeRangeDay',
                  label: 'Date',
                  align: 'left',
                  maxWidth: 300,
                  field: 'appointmentDateTime',
              },
              {
                  id: 'type|string',
                  label: 'Appointment Type',
                  align: 'left',
                  maxWidth: 300,
                  field: 'type',
              },
              {
                  id: 'participants|string',
                  label: 'Assigned To',
                  align: 'left',
                  maxWidth: 300,
                  field: 'participants',
              },
              {
                  id: 'persons|string',
                  label: 'Licensee',
                  align: 'left',
                  maxWidth: 300,
                  field: 'persons',
              },
              {
                  id: 'address|string',
                  label: 'Address',
                  align: 'left',
                  maxWidth: 300,
                  field: 'address',
              },
          ];

    const eventListFormatted = eventList?.map((element) => {
        const appointmentTime = {
            start: element.startDateTime,
            end: element.endDateTime,
        };

        return {
            ...element,
            appointmentDateTime: appointmentTime,
        };
    });

    const events = calendarColumnFields?.fields?.length
        ? getFlatColumnFieldsFromList(eventListFormatted, columns)
        : eventList?.map((element) => {
              const appointmentTime = {
                  start: element.startDateTime,
                  end: element.endDateTime,
              };

              return {
                  ...element,
                  appointmentDateTime: appointmentTime,
                  persons: element?.persons
                      ?.map((person: Person) => person.name)
                      .sort()
                      .join(', '),

                  participants: element?.participants
                      ?.map((participant: User) => participant.name)
                      .sort()
                      .join(', '),

                  address: getFullAddress(element?.address),
                  type: element?.inspectionType?.name
                      ? element?.type?.name + ' - ' + element?.inspectionType?.name || ''
                      : element?.type?.name,
              };
          });

    return (
        <Box className={`${classes.root} verticalFillContainer`}>
            <Box className={classes.metadataContainer}>
                {/* TODO: This is being left in for formatting purposes, remove
                after hotfix. */}
                <Box className={classes.tablePaginationInfo}></Box>
                <span className={classes.exportLicense}>
                    <Box display="flex" className={classes.exportBtn}>
                        <Box display="flex" flexDirection="column" justifyContent="center" zIndex={999}>
                            <Button
                                className={classes.modalBtn}
                                variant="contained"
                                color="primary"
                                ref={anchorRef}
                                aria-controls={open ? 'menu-list-grow' : undefined}
                                aria-haspopup="true"
                                onClick={handleToggle}
                            >
                                <DownloadIcon /> &nbsp;Download
                            </Button>
                            <Menu
                                id="simple-menu"
                                anchorEl={anchorRef.current}
                                keepMounted
                                open={open}
                                onClose={handleClose}
                                className={classes.MenuList}
                            >
                                {mergeTemplatesList?.map((item: any) => (
                                    <MenuItem key={item?.id} onClick={() => handleDownload(item)}>
                                        {item?.name}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    </Box>
                </span>
            </Box>
            <Box className={`${classes.container} verticalFillContent`}>
                {events.length > 0 ? (
                    <InfiniteScrollTableView
                        columns={columns}
                        data={events}
                        count={events.length}
                        onClickRow={onClickRow}
                        hasMore={hasMore}
                        loadMore={loadMore}
                        sortOrder={sortOrder}
                        sortBy={sortBy}
                        setSortColumn={setSortColumn}
                        setSortOrder={setSortOrder}
                    />
                ) : (
                    <TableEmptyState
                        title="No active appointments."
                        message="All your Appointments will be stored here"
                    ></TableEmptyState>
                )}
            </Box>

            {scrollTop > 20 && (
                <Tooltip title="Back to Top" placement="top">
                    <Fab
                        size="small"
                        className={classes.fab}
                        aria-label="edit"
                        onClick={() => {
                            setScrollTop(0);
                        }}
                    >
                        <BackToTopArrow color="primary" />
                    </Fab>
                </Tooltip>
            )}
        </Box>
    );
};

export default EventInfiniteList;
